import { Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { User } from '../models/user/user.model';
import { ScriptManagementService } from '../services/script-management.service';
import { UserService } from '../services/user.service';
import { posthogScript } from '../static-data/external-scripts';
import { FeatureFlags } from './feature-flags';
import { FeatureToken } from './feature-token';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlaggingService {
  private identitySyncSubscription: Subscription;

  private get posthog() {
    return (window as any).posthog;
  }

  constructor(
    private scriptManager: ScriptManagementService,
    private userService: UserService
  ) {}

  init() {
    return new Promise(async (resolve, reject) => {
      const timeoutDuration = 3000;
      // If PostHog never loads, start the app anyway, with every flag off.
      const timeout = setTimeout(() => resolve(null), timeoutDuration);

      await this.scriptManager.load(posthogScript);

      this.posthog.init(environment.posthogApiKey, {
        api_host: environment.posthogApiHost,
        // The CMS only reads feature flags; keep its activity out of slp-web's analytics.
        autocapture: false,
        capture_pageview: false,
        disable_session_recording: true,
        disable_surveys: true,
        // Drops the events PostHog still sends itself ($identify, $feature_flag_called).
        // Email-targeted flags still work, since identify passes the email straight to flag evaluation.
        before_send: () => null,
        // Don't share slp-web's PostHog cookie on everydayspeech.com.
        persistence: 'localStorage',
      });

      this.posthog.onFeatureFlags(() => {
        // Cleared only once flags arrive, so the timeout still lets the app start if PostHog never loads.
        clearTimeout(timeout);
        this.startIdentitySync();
        resolve(this.posthog);
      });
    });
  }

  isFeatureRegistered(FEATURE_TOKEN: FeatureToken) {
    return Object.values(FeatureFlags).includes(FEATURE_TOKEN);
  }

  isFeatureEnabled(FEATURE_TOKEN: FeatureToken) {
    if (!this.isFeatureRegistered(FEATURE_TOKEN)) {
      throw new Error(`Feature token "${FEATURE_TOKEN}" is not registered`);
    }
    return !!this.posthog?.isFeatureEnabled(FEATURE_TOKEN);
  }

  startIdentitySync() {
    if (!this.identitySyncSubscription) {
      this.identitySyncSubscription = this.userService.userSession$.subscribe(event => {
        this.onUserIdentityChanged(event);
      });
    }
  }

  onUserIdentityChanged(event) {
    this.posthog.reloadFeatureFlags();

    if (!event.hasSignedIn) {
      this.resetUserSession();
    } else {
      const user = this.userService.getLocalUser();
      this.identifyUser(user);
    }
  }

  // Capture User for segmentation purposes
  // https://posthog.com/docs/libraries/js#identifying-users
  private identifyUser(user: User) {
    const { email } = user;

    this.posthog?.identify(
      email, // Use email as distinct ID to prevent issues with different environments
      { email }
    );
  }

  // Reset User session after logout to prevent mixing users that use the same device
  // https://posthog.com/docs/libraries/js#reset-after-logout
  private resetUserSession() {
    this.posthog?.reset();
  }
}
